import React from "react";

import Styles from "./tableCells.module.scss";

// Shared cell renderers for every DataGrid in the admin. The tables used to
// render every value as plain text in a flex div, which made scanning a long
// list hard: nothing stood out, and boolean columns read as grey words.

// Deterministic tint per name, so the same team or person keeps the same
// colour between visits without needing a stored value. All six are brand or
// brand-adjacent hues already used elsewhere in the app.
const AVATAR_TINTS = [
  "#0040A8",
  "#363B64",
  "#1B7F4B",
  "#B4661E",
  "#6F48EB",
  "#C32F27",
];

export const initials = (name = "") =>
  String(name)
    .replace(/[^A-Za-z. ]/g, "")
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w.replace(".", "").charAt(0))
    .join("")
    .toUpperCase() || "?";

const tintFor = (name = "") => {
  let sum = 0;
  for (const ch of String(name)) sum += ch.charCodeAt(0);
  return AVATAR_TINTS[sum % AVATAR_TINTS.length];
};

export function Pill({ children, bg, fg, outline }) {
  return (
    <span
      className={Styles.pill}
      style={
        outline
          ? { background: "transparent", color: "#a098ae", borderColor: "#e9ebf1" }
          : { background: bg, color: fg, borderColor: "transparent" }
      }
    >
      {children}
    </span>
  );
}

// "Yes" reads as a solid pill and "No" as a quiet outline, so a glance down
// the column picks out the flagged rows.
export function YesNo({ value, tone = "#0040A8" }) {
  const yes =
    value === true || String(value).toLowerCase() === "yes" || value === 1;
  return yes ? (
    <Pill bg={tone} fg="#ffffff">
      Yes
    </Pill>
  ) : (
    <Pill outline>No</Pill>
  );
}

// Roles are free text (the upload sheet warns they are case sensitive), so the
// tint is picked by keyword. Anything unrecognised falls back to neutral
// rather than going uncoloured.
export function RolePill({ value }) {
  const r = String(value || "").toLowerCase();
  let tint = { bg: "#F1F1F5", fg: "#6E7391" };
  if (r.includes("all")) tint = { bg: "#EEF7F1", fg: "#1B7F4B" };
  else if (r.includes("bowl")) tint = { bg: "#EEF2FB", fg: "#0040A8" };
  else if (r.includes("bat")) tint = { bg: "#FDF3EC", fg: "#B4661E" };
  else if (r.includes("keep") || r.includes("wk"))
    tint = { bg: "#F3EEFB", fg: "#6F48EB" };

  if (!value) return <span className={Styles.muted}>-</span>;
  return (
    <Pill bg={tint.bg} fg={tint.fg}>
      {value}
    </Pill>
  );
}

// `image` is a URL typed into the add/edit form, so it is often blank and
// sometimes wrong. A broken one falls back to the tinted initials rather than
// leaving a torn-image icon in the row.
//
// `fit` is "cover" for photographs (players, people) and "contain" for team
// logos, which are usually transparent artwork that must not be cropped.
export function Avatar({ name, size = 34, image, fit = "cover" }) {
  const [broken, setBroken] = React.useState(false);

  React.useEffect(() => setBroken(false), [image]);

  const showImage = Boolean(image) && !broken;

  return (
    <span
      className={Styles.avatar}
      style={{
        width: size,
        height: size,
        background: showImage && fit === "contain" ? "#ffffff" : tintFor(name),
        border: showImage && fit === "contain" ? "1px solid #eff0f6" : "none",
        fontSize: size <= 26 ? 9 : 11,
      }}
    >
      {showImage ? (
        <img
          className={Styles.avatarImg}
          src={image}
          alt=""
          style={{ objectFit: fit, padding: fit === "contain" ? 3 : 0 }}
          onError={() => setBroken(true)}
        />
      ) : (
        initials(name)
      )}
    </span>
  );
}

// Name plus avatar, with an optional trailing action that only appears on row
// hover so it does not compete with the name.
export function NameCell({ name, sub, action, image, fit }) {
  return (
    <div className={Styles.nameCell}>
      <Avatar name={name} image={image} fit={fit} />
      <span className={Styles.nameText}>
        <span className={Styles.name}>{name || "-"}</span>
        {sub ? <span className={Styles.sub}>{sub}</span> : null}
      </span>
      {action ? <span className={Styles.action}>{action}</span> : null}
    </div>
  );
}

export function Muted({ children }) {
  return <span className={Styles.muted}>{children || "-"}</span>;
}

export function Strong({ children }) {
  return <span className={Styles.strong}>{children}</span>;
}
